import type { Encounter } from '@medplum/fhirtypes'
import { DAY_END, DAY_START } from './scheduleCalendar.tokens'

const VIRTUAL_VISIT_CLASS = 'http://terminology.hl7.org/CodeSystem/v3-ActCode|VR'

export const APPOINTMENT_SEARCH_LIMIT = 5

export type SearchDirection = 'upcoming' | 'past'

export interface AppointmentSearchResult {
  encounterId: string
  patientId?: string
  patientName: string
  practitionerId?: string
  start: string
  end: string
}

export function buildAppointmentSearchQuery(
  patientIds: string[],
  practitionerIds: string[],
  direction: SearchDirection,
  referenceIso: string,
): string {
  const upcoming = direction === 'upcoming'
  return [
    `subject=${patientIds.map((id) => `Patient/${id}`).join(',')}`,
    `practitioner=${practitionerIds.map((id) => `Practitioner/${id}`).join(',')}`,
    'status:not=cancelled',
    `class=${VIRTUAL_VISIT_CLASS}`,
    upcoming ? `date=ge${referenceIso}` : `date=lt${referenceIso}`,
    upcoming ? '_sort=date' : '_sort=-date',
    `_count=${APPOINTMENT_SEARCH_LIMIT}`,
  ].join('&')
}

export function toAppointmentSearchResults(encounters: Encounter[]): AppointmentSearchResult[] {
  const results: AppointmentSearchResult[] = []
  for (const encounter of encounters) {
    const start = encounter.period?.start
    if (!encounter.id || !start) {
      continue
    }
    results.push({
      encounterId: encounter.id,
      patientId: encounter.subject?.reference?.split('/')[1],
      patientName: encounter.subject?.display || 'Unknown Patient',
      practitionerId: encounter.participant
        ?.find((participant) => participant.individual?.reference?.startsWith('Practitioner/'))
        ?.individual?.reference?.split('/')[1],
      start,
      end: encounter.period?.end || '',
    })
  }
  return results
}

export function sortByProximity(
  results: AppointmentSearchResult[],
  referenceMs: number,
): AppointmentSearchResult[] {
  return [...results].sort(
    (a, b) =>
      Math.abs(new Date(a.start).getTime() - referenceMs) -
      Math.abs(new Date(b.start).getTime() - referenceMs),
  )
}

export function fallsOutsideGridHours(start: string): boolean {
  const date = new Date(start)
  const slot = date.getHours() + (date.getMinutes() >= 30 ? 0.5 : 0)
  return slot < DAY_START || slot >= DAY_END
}
